import AppDropdown from '@/components/AppDropdown';
import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { createBlockMsisdn, getBlockMsisdn } from '@/services/block.service';
import { columnSorter } from '@/utils/formatters';
import { EllipsisOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Button, Modal, Space } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const columns = [
  {
    title: 'MSISDN',
    dataIndex: 'isdn',
    key: 'isdn',
    align: 'left',
    width: '90%',
    sorter: columnSorter('isdn'),
    render: (value) => (value ? value : '-'),
  },
];

function MsisdnBlock() {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [queryValues, setQueryValues] = useState({
    isdn: null,
  });
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const offset = (pageIndex - 1) * pageSize + 1;

  const {
    data: blockData,
    isLoading,
    refetch,
    isRefetching,
  } = usePaginatedQuery(
    () =>
      getBlockMsisdn({
        isdn: queryValues.isdn,
        limit: pageSize,
        offset: offset,
      }),
    ['blockMsisdn', queryValues, pageIndex, offset]
  );

  const { mutate: unBlock, isPending } = useMutation({
    mutationFn: createBlockMsisdn,
    onSuccess: () => {
      toast.success('Successfully unblocked selected ISDNs!');
      if (selectedRowKeys.length > 0) setSelectedRowKeys([]);
      refetch();
    },
    onError: (error) => {
      const errorMessage = error?.data?.errorMessage || 'Something went wrong';
      toast.error(errorMessage);
    },
  });

  const handleQuery = () => {
    setQueryValues({
      isdn: searchValue,
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setSearchValue(null);
    setQueryValues({
      isdn: null,
    });
    setPageIndex(1);
  };

  const handleOnRefresh = () => {
    handleReset();
    refetch();
  };

  const handleOnCreate = () => navigate(PATH.MSISDN_BLOCK.CREATE);

  const handleUnblockItem = (item) => {
    Modal.confirm({
      title: 'Unblock ISDN',
      content: `Are you sure you want to unblock "${item.isdn}"?`,
      onOk: () => {
        unBlock({
          id: [item.id],
        });
      },
      okButtonProps: {
        loading: isPending,
        disabled: isPending,
      },
      cancelButtonProps: {
        disabled: isPending,
      },
    });
  };

  const handleUnblockSelectedItems = () => {
    if (selectedRowKeys.length === 0) {
      Modal.warning({
        title: 'No ISDN(s) selected',
        content: 'Please select at least one ISDN to delete.',
      });
      return;
    }

    Modal.confirm({
      title: 'Unblock selected ISDN(s)',
      content: `Are you sure you want to unblock ${selectedRowKeys.length} selected ISDN(s)?`,
      onOk: () => {
        unBlock({
          id: selectedRowKeys,
        });
      },
      okButtonProps: {
        loading: isPending,
        disabled: isPending,
      },
      cancelButtonProps: {
        disabled: isPending,
      },
    });
  };

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="MSISDN Block" />
      <AppTableQuery
        hasFilter={false}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleQuery={handleQuery}
        handleReset={handleReset}
        searchPlaceholder="Search MSISDN"
      />
      <AppTable
        columns={columns}
        dataSource={blockData?.data}
        totalItems={blockData?.total}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
        onCreate={handleOnCreate}
        hasRowSelect
        rowKey={(record) => record.id}
        tableTitle={<p className="font-semibold">List MSISDN</p>}
        loading={isLoading || isRefetching}
        onRefresh={handleOnRefresh}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        showSizeChanger={false}
        hasRowAction
        onRemoveItems={() => handleUnblockSelectedItems()}
        removeItemsLabel="Unblock"
        rowAction={(record) => (
          <AppDropdown
            options={[
              {
                key: '1',
                label: 'Unblock',
                onClick: () => handleUnblockItem(record),
              },
            ]}
            onChange={(item) => item.onClick()}
            placement="bottomRight"
            overlayStyle={{ minWidth: '150px' }}
          >
            <Button type="text" className="px-2">
              <EllipsisOutlined />
            </Button>
          </AppDropdown>
        )}
      />
    </Space>
  );
}

export default MsisdnBlock;
